import moment from 'moment';
import Swal from 'sweetalert2';
import HelperMixin from '../../../Shared/HelperMixin.vue';
import PaginationHelper from '../../../Shared/PaginationHelper.vue';

const STORAGE_KEY_FIELDS = 'productsReports.fields.v1';
const STORAGE_KEY_TEMPLATES = 'productsReports.userTemplates.v1';

export default {
    name: 'ProductsReports',
    mixins: [HelperMixin],
    components: { PaginationHelper },

    async created() {
        await this.CheckLoginStatus();
        if (!this.loginDetails) { this.logout(); return; }
        await this.loadFilters();
        await this.loadTemplates();
        this.loadUserTemplates();
        this.applyDefaultFields();
        await this.generateReport(1);
    },

    data() {
        return {
            loading: false,
            exporting: false,
            lookups: {
                categories: [],
                manufacturers: [],
                countries: [],
                stockings: [],
                fields: [],
                productStatuses: [],
                expiryStatuses: [],
                groupings: []
            },
            reportTypes: [
                { id: 'products', name: 'تقرير منتجات' },
                { id: 'inventory', name: 'تقرير مخزون' },
                { id: 'movements', name: 'تقرير حركات' },
                { id: 'custom', name: 'تقرير مخصص' }
            ],
            scheduleFrequencies: [
                { id: 'daily', name: 'يومي' },
                { id: 'weekly', name: 'أسبوعي' },
                { id: 'monthly', name: 'شهري' },
                { id: 'quarterly', name: 'ربع سنوي' }
            ],
            form: {
                reportType: 'products',
                fromDate: '',
                toDate: '',
                categoryId: '',
                manufacturerId: '',
                countryId: '',
                stockingId: '',
                productStatus: 'all',
                expiryStatus: 'all',
                keywords: '',
                fields: [],
                sortBy: 'ProductName',
                sortDir: 'asc',
                groupBy: 'none',
                showTotals: true,
                pageNo: 1,
                pageSize: 25
            },
            exportOptions: {
                paperSize: 'A4',
                orientation: 'portrait',
                includeDate: true,
                includePageNumbers: true,
                includeLogo: false
            },
            schedule: {
                enabled: false,
                frequency: 'monthly',
                startOn: '',
                endOn: '',
                emailsRaw: '',
                format: 'pdf'
            },
            templates: [],
            userTemplates: [],
            activeTemplateId: '',
            rows: [],
            totalCount: 0,
            totalPages: 0,
            totals: {},
            groups: [],
            usedFieldsServer: [],
            newTemplate: { name: '', description: '' },
            emailToSend: { recipients: '', format: 'pdf' },
            saveTemplateModal: null,
            emailModal: null,
            hasSavedFields: false
        };
    },

    computed: {
        selectedFieldDefs() {
            const map = new Map((this.lookups.fields || []).map(f => [f.key, f]));
            return (this.form.fields || []).map(k => map.get(k)).filter(Boolean);
        },
        groupingLabel() {
            const g = (this.lookups.groupings || []).find(x => x.id === this.form.groupBy);
            return g ? g.name : 'بدون';
        }
    },

    methods: {
        applyDefaultFields() {
            if (this.form.fields && this.form.fields.length) return;
            const saved = this.readSavedFields();
            if (saved && saved.length) {
                this.form.fields = saved;
                return;
            }
            this.form.fields = (this.lookups.fields || []).filter(f => f.defaultSelected).map(f => f.key);
            if (!this.form.fields.length && this.lookups.fields) {
                this.form.fields = this.lookups.fields.map(f => f.key);
            }
        },

        readSavedFields() {
            try {
                const raw = localStorage.getItem(STORAGE_KEY_FIELDS);
                if (!raw) return null;
                const arr = JSON.parse(raw);
                this.hasSavedFields = Array.isArray(arr) && arr.length > 0;
                return arr;
            } catch (e) {
                return null;
            }
        },

        saveFieldSelection() {
            try {
                localStorage.setItem(STORAGE_KEY_FIELDS, JSON.stringify(this.form.fields || []));
                this.hasSavedFields = (this.form.fields || []).length > 0;
                this.$helper.ShowMessage('success', 'تم الحفظ', 'تم حفظ اختياراتك للحقول محلياً.');
            } catch (e) {
                this.$helper.ShowMessage('error', 'تعذّر الحفظ', 'لم يتمكن المتصفح من حفظ الإعدادات.');
            }
        },

        loadFieldSelection() {
            const saved = this.readSavedFields();
            if (saved && saved.length) {
                this.form.fields = saved;
                this.$helper.ShowMessage('success', 'تم التحميل', 'تم تحميل اختياراتك المحفوظة.');
            }
        },

        readUserTemplates() {
            try {
                const raw = localStorage.getItem(STORAGE_KEY_TEMPLATES);
                if (!raw) return [];
                const arr = JSON.parse(raw);
                return Array.isArray(arr) ? arr : [];
            } catch (e) {
                return [];
            }
        },

        loadUserTemplates() {
            this.userTemplates = this.readUserTemplates();
        },

        persistUserTemplates() {
            try { localStorage.setItem(STORAGE_KEY_TEMPLATES, JSON.stringify(this.userTemplates)); } catch (e) { /* ignore */ }
        },

        async loadFilters() {
            try {
                const resp = await this.$http.GetProductsReportFilters();
                const d = resp.data || {};
                this.lookups.categories = d.categories || [];
                this.lookups.manufacturers = d.manufacturers || [];
                this.lookups.countries = d.countries || [];
                this.lookups.stockings = d.stockings || [];
                this.lookups.fields = d.fields || [];
                this.lookups.productStatuses = d.productStatuses || [];
                this.lookups.expiryStatuses = d.expiryStatuses || [];
                this.lookups.groupings = d.groupings || [];
                if (Array.isArray(d.reportTypes) && d.reportTypes.length) this.reportTypes = d.reportTypes;
            } catch (e) {
                this.showRequestError('تعذّر تحميل بيانات الفلاتر', e);
            }
        },

        async loadTemplates() {
            try {
                const resp = await this.$http.GetProductsReportTemplates();
                this.templates = (resp.data && resp.data.info) || [];
            } catch (e) {
                this.templates = [];
            }
        },

        reportTypeLabel(id) {
            const found = (this.reportTypes || []).find(r => r.id === id);
            return found ? found.name : (id || '');
        },

        isFieldSelected(key) {
            return (this.form.fields || []).indexOf(key) !== -1;
        },

        selectAllFields() {
            this.form.fields = (this.lookups.fields || []).map(f => f.key);
        },

        clearAllFields() {
            this.form.fields = [];
        },

        applyTemplate(tpl) {
            this.activeTemplateId = tpl.id;
            const cfg = (tpl && tpl.config) || {};
            this.form.reportType = cfg.reportType || this.form.reportType;
            if (Array.isArray(cfg.fields) && cfg.fields.length) this.form.fields = cfg.fields.slice();
            if (cfg.sortBy) this.form.sortBy = cfg.sortBy;
            if (cfg.sortDir) this.form.sortDir = cfg.sortDir;
            if (cfg.groupBy) this.form.groupBy = cfg.groupBy;
            if (cfg.productStatus) this.form.productStatus = cfg.productStatus;
            if (cfg.expiryStatus) this.form.expiryStatus = cfg.expiryStatus;
            this.generateReport(1);
        },

        applyUserTemplate(tpl) {
            this.applyTemplate(tpl);
        },

        deleteUserTemplate(tpl) {
            Swal.fire({
                title: 'حذف القالب',
                text: 'هل تريد حذف "' + tpl.name + '"؟',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'حذف',
                cancelButtonText: 'إلغاء',
                customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-label-secondary ms-2' },
                buttonsStyling: false
            }).then(r => {
                if (!r.isConfirmed) return;
                this.userTemplates = this.userTemplates.filter(x => x.id !== tpl.id);
                this.persistUserTemplates();
            });
        },

        openSaveTemplateModal() {
            this.newTemplate = { name: '', description: '' };
            this.openModal('saveTemplateModal');
        },

        openEmailModal() {
            this.emailToSend = { recipients: '', format: 'pdf' };
            this.openModal('emailReportModal');
        },

        openModal(id) {
            const el = document.getElementById(id);
            if (!el || !window.bootstrap || !window.bootstrap.Modal) return;
            const modal = window.bootstrap.Modal.getOrCreateInstance(el);
            modal.show();
        },

        closeModal(id) {
            const el = document.getElementById(id);
            if (!el || !window.bootstrap || !window.bootstrap.Modal) return;
            const modal = window.bootstrap.Modal.getOrCreateInstance(el);
            modal.hide();
        },

        async saveTemplate() {
            if (!this.newTemplate.name || this.newTemplate.name.trim().length < 2) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'يرجى إدخال اسم للقالب.');
                return;
            }
            const payload = {
                name: this.newTemplate.name.trim(),
                description: (this.newTemplate.description || '').trim(),
                report: this.buildReportBody()
            };
            try {
                const resp = await this.$http.SaveProductsReportTemplate(payload);
                const saved = resp.data || {};
                this.userTemplates.unshift({
                    id: saved.id || ('user-' + Date.now()),
                    name: payload.name,
                    description: payload.description,
                    config: this.serializeConfig()
                });
                this.persistUserTemplates();
                this.closeModal('saveTemplateModal');
                this.$helper.ShowMessage('success', 'تم الحفظ', 'تم حفظ القالب الجديد.');
            } catch (e) {
                this.showRequestError('تعذّر حفظ القالب', e);
            }
        },

        serializeConfig() {
            return {
                reportType: this.form.reportType,
                fields: (this.form.fields || []).slice(),
                sortBy: this.form.sortBy,
                sortDir: this.form.sortDir,
                groupBy: this.form.groupBy,
                productStatus: this.form.productStatus,
                expiryStatus: this.form.expiryStatus
            };
        },

        buildReportBody() {
            return {
                reportType: this.form.reportType,
                fromDate: this.form.fromDate || null,
                toDate: this.form.toDate || null,
                categoryId: this.form.categoryId || null,
                manufacturerId: this.form.manufacturerId || null,
                countryId: this.form.countryId || null,
                stockingId: this.form.stockingId || null,
                productStatus: this.form.productStatus,
                expiryStatus: this.form.expiryStatus,
                keywords: this.form.keywords || '',
                fields: this.form.fields || [],
                sortBy: this.form.sortBy,
                sortDir: this.form.sortDir,
                groupBy: this.form.groupBy,
                showTotals: !!this.form.showTotals,
                pageNo: this.form.pageNo || 1,
                pageSize: this.form.pageSize || 25
            };
        },

        async generateReport(page) {
            this.form.pageNo = page || this.form.pageNo || 1;
            this.loading = true;
            try {
                const body = this.buildReportBody();
                const resp = await this.$http.GenerateProductsReport(body);
                const d = resp.data || {};
                this.rows = Array.isArray(d.info) ? d.info : [];
                this.totalCount = d.count || 0;
                this.totalPages = d.pages || 0;
                this.totals = d.totals || {};
                this.groups = Array.isArray(d.groups) ? d.groups : [];
                this.usedFieldsServer = Array.isArray(d.fields) ? d.fields : (this.form.fields || []);
            } catch (e) {
                this.rows = [];
                this.totalCount = 0;
                this.totals = {};
                this.groups = [];
                this.showRequestError('تعذّر توليد التقرير', e);
            } finally {
                this.loading = false;
            }
        },

        changePage(p) {
            this.generateReport(p);
        },

        toggleSort(key) {
            if (this.form.sortBy === key) {
                this.form.sortDir = this.form.sortDir === 'asc' ? 'desc' : 'asc';
            } else {
                this.form.sortBy = key;
                this.form.sortDir = 'asc';
            }
            this.generateReport(1);
        },

        rowSequenceNumber(idx) {
            return ((this.form.pageNo - 1) * this.form.pageSize) + idx + 1;
        },

        groupChanges(idx) {
            if (!this.form.groupBy || this.form.groupBy === 'none') return false;
            const fieldKey = this.groupFieldKey();
            if (!fieldKey) return false;
            if (idx === 0) return true;
            const prev = this.rows[idx - 1] && this.rows[idx - 1][fieldKey];
            const curr = this.rows[idx] && this.rows[idx][fieldKey];
            return (prev || '') !== (curr || '');
        },

        groupValueAt(idx) {
            const key = this.groupFieldKey();
            if (!key) return '';
            const r = this.rows[idx];
            return r ? (r[key] || '') : '';
        },

        groupFieldKey() {
            switch (this.form.groupBy) {
                case 'category': return 'Categories';
                case 'manufacturer': return 'ManufacturerName';
                case 'country': return 'CountryName';
                case 'warehouse': return 'StockingName';
                default: return '';
            }
        },

        formatNumber(v) {
            if (v === null || v === undefined || v === '') return '0';
            const n = Number(v);
            if (!Number.isFinite(n)) return String(v);
            return n.toLocaleString('ar');
        },

        formatCell(value, key) {
            if (value === null || value === undefined || value === '') return '-';
            if (this.isDateField(key)) {
                const m = moment(value);
                if (m.isValid()) return m.format('YYYY-MM-DD');
            }
            if (this.isNumberField(key)) return this.formatNumber(value);
            return value;
        },

        isDateField(key) {
            return ['CreatedOn', 'LastDispatchDate', 'LastAdditionDate', 'ExpiryDate'].indexOf(key) !== -1;
        },

        isNumberField(key) {
            return ['TotalQuantity', 'StockQuantity', 'BatchCount', 'StockValue', 'AveragePrice', 'MinPrice', 'MaxPrice', 'StockPrice', 'LastDispatchedQty'].indexOf(key) !== -1;
        },

        resetFilters() {
            this.form.fromDate = '';
            this.form.toDate = '';
            this.form.categoryId = '';
            this.form.manufacturerId = '';
            this.form.countryId = '';
            this.form.stockingId = '';
            this.form.productStatus = 'all';
            this.form.expiryStatus = 'all';
            this.form.keywords = '';
            this.form.groupBy = 'none';
            this.form.sortBy = 'ProductName';
            this.form.sortDir = 'asc';
            this.activeTemplateId = '';
            this.applyDefaultFields();
            this.generateReport(1);
        },

        async exportExcel() {
            if (this.exporting) return;
            this.exporting = true;
            try {
                const resp = await this.$http.ExportProductsReportExcel(this.buildReportBody());
                this.downloadBlob(resp.data, this.fileName('xlsx'));
            } catch (e) {
                this.showRequestError('تعذّر تصدير Excel', e);
            } finally {
                this.exporting = false;
            }
        },

        async exportCsv() {
            if (this.exporting) return;
            this.exporting = true;
            try {
                const resp = await this.$http.ExportProductsReportCsv(this.buildReportBody());
                this.downloadBlob(resp.data, this.fileName('csv'));
            } catch (e) {
                this.showRequestError('تعذّر تصدير CSV', e);
            } finally {
                this.exporting = false;
            }
        },

        async exportPdf() {
            if (this.exporting) return;
            this.exporting = true;
            try {
                const resp = await this.$http.ExportProductsReportPdf(this.buildReportBody());
                const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'text/html;charset=utf-8' }));
                const w = window.open(url, '_blank');
                if (!w) {
                    this.$helper.ShowMessage('warning', 'منع المتصفح فتح النافذة', 'يرجى السماح بفتح النوافذ المنبثقة لطباعة/حفظ PDF.');
                }
            } catch (e) {
                this.showRequestError('تعذّر تصدير PDF', e);
            } finally {
                this.exporting = false;
            }
        },

        printReport() {
            window.print();
        },

        async sendEmail() {
            const recips = (this.emailToSend.recipients || '')
                .split(/[,;\n]/)
                .map(x => x.trim())
                .filter(x => x.length > 0);
            if (!recips.length) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'يرجى إدخال بريد إلكتروني واحد على الأقل.');
                return;
            }
            const payload = {
                enabled: true,
                frequency: 'once',
                startOn: new Date().toISOString().slice(0, 10),
                endOn: null,
                emails: recips,
                format: this.emailToSend.format || 'pdf',
                report: this.buildReportBody()
            };
            try {
                await this.$http.SaveProductsReportSchedule(payload);
                this.closeModal('emailReportModal');
                this.$helper.ShowMessage('success', 'تم الإرسال', 'تم تسجيل طلب الإرسال للتنفيذ.');
            } catch (e) {
                this.showRequestError('تعذّر إرسال التقرير', e);
            }
        },

        async saveSchedule() {
            const recips = (this.schedule.emailsRaw || '').split(/[,;\n]/).map(x => x.trim()).filter(x => x);
            if (this.schedule.enabled && !this.schedule.frequency) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'يرجى اختيار التكرار.');
                return;
            }
            if (this.schedule.enabled && recips.length === 0) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'أدخل بريداً إلكترونياً واحداً على الأقل.');
                return;
            }
            const payload = {
                enabled: !!this.schedule.enabled,
                frequency: this.schedule.frequency,
                startOn: this.schedule.startOn || null,
                endOn: this.schedule.endOn || null,
                emails: recips,
                format: this.schedule.format || 'pdf',
                report: this.buildReportBody()
            };
            try {
                await this.$http.SaveProductsReportSchedule(payload);
                this.$helper.ShowMessage('success', 'تم الحفظ', 'تم حفظ إعدادات الجدولة.');
            } catch (e) {
                this.showRequestError('تعذّر حفظ الجدولة', e);
            }
        },

        cancelSchedule() {
            this.schedule.enabled = false;
        },

        fileName(ext) {
            const stamp = moment().format('YYYYMMDD-HHmmss');
            return 'ProductsReport-' + stamp + '.' + ext;
        },

        downloadBlob(data, name) {
            const url = window.URL.createObjectURL(new Blob([data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = name;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            a.remove();
        },

        showRequestError(title, err) {
            const message = (err && err.response && err.response.data) ? err.response.data : 'حدث خطأ غير متوقع';
            const text = typeof message === 'string' ? message : (message && message.message) || 'حدث خطأ غير متوقع';
            this.$helper.ShowMessage('error', title, text);
        }
    }
};
